import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Server, ExternalLink, Printer, ShieldCheck, Building2, Landmark,
  ArrowRight, GitBranch, Cpu, Lock, Info, CheckCircle, BookOpen, FlaskConical,
} from 'lucide-react';
import {
  VERIFIED, KEY_LINKS, CORE_SERVICES, SUPPORTING_STACK, DEPLOYMENT_PATHS,
  SIZING_TIERS, SECURITY_ITEMS, SANDBOX_TO_PRODUCTION, VNEXT_NOTE, DISCLAIMER,
} from '../../data/mojaloopDeployment';

/**
 * Switch Deployment — the CoE's curated reference for the production Mojaloop
 * switch: what a certified DFSP will connect to, and how a scheme operator
 * actually deploys it. All content (versions, links, sizing) comes from the
 * data catalog in src/data/mojaloopDeployment.js — update it there.
 */

const Ext = ({ url, children }) => (
  <a href={url} target="_blank" rel="noreferrer"
     className="inline-flex items-center gap-1 text-primary-600 hover:underline">
    {children} <ExternalLink className="w-3 h-3" />
  </a>
);

function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-primary-600" /> {children}
    </h2>
  );
}

/** Standalone printable document built from the same catalog. */
function printGuide() {
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const html = `<!doctype html><html><head><title>Mojaloop Switch Deployment Reference — AfricaNenda IIPS Sandbox</title>
  <style>
    body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:2rem auto;max-width:52rem;color:#111;line-height:1.45;font-size:13px}
    h1{font-size:20px;margin-bottom:0} h2{font-size:15px;margin-top:1.6em;border-bottom:1px solid #ddd;padding-bottom:4px}
    .meta{color:#666;font-size:11px} table{border-collapse:collapse;width:100%;font-size:12px}
    td,th{border:1px solid #ccc;padding:6px;text-align:left;vertical-align:top}
    .note{background:#f6f6f6;border-left:3px solid #999;padding:8px;font-size:12px}
    li{margin-bottom:3px} a{color:#0a4d8c}
  </style></head><body>
  <h1>Mojaloop Switch Deployment Reference</h1>
  <p class="meta">AfricaNenda IIPS Sandbox · verified against ${esc(VERIFIED.mojaloopVersion)} on ${esc(VERIFIED.verifiedOn)} · authoritative source: ${esc(VERIFIED.authoritative)}</p>
  <div class="note">${esc(DISCLAIMER)}</div>
  <h2>Core hub services</h2>
  <table><tr><th>Service</th><th>Phase</th><th>Role</th></tr>
  ${CORE_SERVICES.map(s => `<tr><td>${esc(s.name)}<br/><a href="${s.repo}">${s.repo}</a></td><td>${esc(s.phase)}</td><td>${esc(s.role)}</td></tr>`).join('')}
  </table>
  <h2>Supporting stack</h2>
  <ul>${SUPPORTING_STACK.map(s => `<li><b>${esc(s.name)}</b> — ${esc(s.role)}</li>`).join('')}</ul>
  <h2>Deployment paths</h2>
  ${DEPLOYMENT_PATHS.map(p => `<h3>${esc(p.title)}</h3>
    <p><b>Tool:</b> ${esc(p.tool)} · <a href="${p.repo}">${p.repo}</a> · <a href="${p.docs}">docs</a></p>
    <p>${esc(p.summary)}</p><ol>${p.steps.map(st => `<li>${esc(st)}</li>`).join('')}</ol>`).join('')}
  <h2>Hardware sizing (starting points)</h2>
  <table><tr><th>Tier</th><th>Shape</th><th>Notes</th></tr>
  ${SIZING_TIERS.map(t => `<tr><td>${esc(t.tier)}</td><td>${esc(t.shape)}</td><td>${esc(t.note)}</td></tr>`).join('')}
  </table>
  <h2>Security &amp; scheme hardening</h2>
  <ul>${SECURITY_ITEMS.map(s => `<li><b>${esc(s.title)}.</b> ${esc(s.body)}</li>`).join('')}</ul>
  <h2>Sandbox → production mapping</h2>
  <table><tr><th>In this sandbox</th><th>At the production switch</th></tr>
  ${SANDBOX_TO_PRODUCTION.map(m => `<tr><td>${esc(m.sandbox)}</td><td>${esc(m.production)}</td></tr>`).join('')}
  </table>
  <h2>vNext</h2><p>${esc(VNEXT_NOTE)}</p>
  <h2>Key links</h2>
  <ul>${KEY_LINKS.map(l => `<li><a href="${l.url}">${esc(l.name)}</a> — ${l.url}</li>`).join('')}</ul>
  </body></html>`;
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
}

export default function SwitchDeployment() {
  // Two lenses on the same reference: DFSPs connect to the switch; scheme
  // operators deploy it.
  const [audience, setAudience] = useState('dfsp');

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Server className="w-6 h-6 text-primary-600" /> Switch Deployment
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            What the production Mojaloop switch looks like and how it is properly deployed —
            AfricaNenda's curated map into the official Mojaloop resources.
          </p>
        </div>
        <button onClick={printGuide}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 shrink-0">
          <Printer className="w-4 h-4" /> Print / save as PDF
        </button>
      </div>

      {/* Verified stamp + disclaimer */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-800 font-medium">
            <CheckCircle className="w-3.5 h-3.5" /> Verified against Mojaloop {VERIFIED.mojaloopVersion} · {VERIFIED.verifiedOn}
          </span>
          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600">Helm: {VERIFIED.helmChartRelease}</span>
          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600">ISO 20022 native since {VERIFIED.isoSince}</span>
        </div>
        <p className="text-xs text-gray-500 mt-2">{DISCLAIMER}</p>
      </div>

      {/* Audience toggle */}
      <div className="flex gap-2">
        {[
          ['dfsp', Building2, 'I integrate a DFSP', 'What you will connect to in production'],
          ['operator', Landmark, 'I operate a scheme / hub', 'How to actually deploy the switch'],
        ].map(([key, Icon, label, sub]) => (
          <button key={key} onClick={() => setAudience(key)}
            className={`flex-1 text-left rounded-xl border p-3 transition-colors ${
              audience === key
                ? 'border-primary-300 bg-primary-50'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}>
            <p className={`text-sm font-semibold flex items-center gap-2 ${
              audience === key ? 'text-primary-800' : 'text-gray-800'}`}>
              <Icon className="w-4 h-4" /> {label}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
          </button>
        ))}
      </div>

      {/* ── DFSP track ─────────────────────────────────────────────────── */}
      {audience === 'dfsp' && (
        <>
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
            <p className="text-sm text-primary-900">
              As a participant DFSP you do <strong>not</strong> deploy the switch — the scheme
              operator does. What matters to you is what terminates your connection: the hub
              services below, the security they enforce, and how the flows you certified in this
              sandbox map onto them.
            </p>
          </div>

          {/* Architecture */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={GitBranch}>The hub services your traffic touches</SectionTitle>
            <div className="space-y-2">
              {CORE_SERVICES.map(s => (
                <div key={s.name} className="border border-gray-100 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-gray-800">{s.name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-100">{s.phase}</span>
                    <span className="ml-auto"><Ext url={s.repo}>repository</Ext></span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{s.role}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Underneath sit Kafka, MySQL/Percona, Redis, MongoDB, an ingress/service mesh,
              Vault, Keycloak and the monitoring stack — the scheme operator's concern, listed
              in full under the operator lens.
            </p>
          </div>

          {/* Security expectations */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={Lock}>Security the production hub will enforce on you</SectionTitle>
            <div className="space-y-3">
              {SECURITY_ITEMS.map(s => (
                <div key={s.title}>
                  <p className="text-sm font-medium text-gray-800">{s.title}</p>
                  <p className="text-xs text-gray-600">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mapping table */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={ArrowRight}>How this sandbox maps to the production switch</SectionTitle>
            <p className="text-xs text-gray-500 mb-3">
              Every exchange you certified here has a direct counterpart at a real Mojaloop hub —
              including the callback-first behaviour the outbound round tests.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase tracking-wide">
                    <th className="py-2 pr-4 font-medium w-1/2">In this sandbox</th>
                    <th className="py-2 font-medium">At the production switch</th>
                  </tr>
                </thead>
                <tbody>
                  {SANDBOX_TO_PRODUCTION.map((m, i) => (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="py-2.5 pr-4 text-gray-700">{m.sandbox}</td>
                      <td className="py-2.5 text-gray-700">{m.production}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Operator track ─────────────────────────────────────────────── */}
      {audience === 'operator' && (
        <>
          {/* Deployment paths */}
          <div className="space-y-4">
            {DEPLOYMENT_PATHS.map((p, i) => (
              <div key={p.key} className={`bg-white rounded-xl border p-5 ${
                p.key === 'production' ? 'border-primary-300' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <h2 className="text-sm font-semibold text-gray-900">{p.title}</h2>
                  {p.key === 'production' && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-200">recommended for go-live</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">For: {p.audience} · Tool: {p.tool}</p>
                <p className="text-sm text-gray-700 mt-2">{p.summary}</p>
                <ol className="mt-3 space-y-1.5">
                  {p.steps.map((s, n) => (
                    <li key={n} className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="font-mono text-gray-400 shrink-0">{n + 1}.</span> {s}
                    </li>
                  ))}
                </ol>
                <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100 text-xs">
                  <Ext url={p.repo}>GitHub repository</Ext>
                  <Ext url={p.docs}>Official docs</Ext>
                </div>
              </div>
            ))}
          </div>

          {/* Sizing */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={Cpu}>Hardware sizing — starting points, not targets</SectionTitle>
            <div className="space-y-2">
              {SIZING_TIERS.map(t => (
                <div key={t.tier} className="border border-gray-100 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-800">{t.tier}</p>
                    <p className="font-mono text-xs text-gray-600">{t.shape}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Supporting stack */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={Server}>The supporting stack you also operate</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SUPPORTING_STACK.map(s => (
                <div key={s.name} className="border border-gray-100 bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-800">{s.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.role}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Security */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={Lock}>Security & scheme hardening</SectionTitle>
            <div className="space-y-3">
              {SECURITY_ITEMS.map(s => (
                <div key={s.title}>
                  <p className="text-sm font-medium text-gray-800">{s.title}</p>
                  <p className="text-xs text-gray-600">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Validation */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <SectionTitle icon={FlaskConical}>Validating a deployment</SectionTitle>
            <p className="text-sm text-gray-700">
              Every environment — lab to production — should prove itself with the{' '}
              <Ext url="https://github.com/mojaloop/ml-testing-toolkit">Mojaloop Testing Toolkit</Ext>{' '}
              Golden Path collections: simulated DFSPs exercising discovery, agreement, transfer
              and settlement end-to-end. It plays the same role hub-side that this sandbox's{' '}
              <Link to="/iso20022/certification" className="text-primary-600 hover:underline">certification suite</Link>{' '}
              plays for participants — and your DFSP onboarding gate should require both.
            </p>
          </div>

          {/* vNext */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-900">{VNEXT_NOTE}</p>
          </div>
        </>
      )}

      {/* Key links (both lenses) */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <SectionTitle icon={BookOpen}>Key resources</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {KEY_LINKS.map(l => (
            <p key={l.url} className="text-sm"><Ext url={l.url}>{l.name}</Ext></p>
          ))}
        </div>
      </div>

      {/* CTA back to certification */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-blue-900 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          Everything here assumes a certified integration — prove yours on the Certification page first.
        </p>
        <Link to="/iso20022/certification"
          className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
          Go to Certification <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
